// frontend/src/pages/AuthLoading.js
import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useUser } from '../UserContext';
import { CircularProgress, Box, Typography } from '@mui/material';
import { normalizeTierName, isConsultantTier } from '../utils/tierHelpers';
import { triggerAuthRecovery } from '../utils/authRecovery';

/**
 * AuthLoading - Post-login routing component
 * 
 * Determines where to route existing users based on their accountType from database.
 * This is the single source of truth for routing after authentication.
 * 
 * Flow:
 * 1. User logs in → Entra auth
 * 2. RedirectHandler sends to /auth/loading
 * 3. This component loads profile from database
 * 4. Routes to correct dashboard based on accountType
 */
export default function AuthLoading() {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const { userProfile, orgProfile, subscriptionState, profileLoading, refreshProfile } = useUser();
  const hasTriggeredRefresh = useRef(false);
  const hasTriggeredRecovery = useRef(false);
  const hasAttemptedInitialRefresh = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (hasTriggeredRecovery.current) {
        return;
      }

      if (profileLoading) {
        return;
      }

      const missingProfile = !userProfile || !orgProfile;
      if (missingProfile) {
        if (!hasAttemptedInitialRefresh.current) {
          hasAttemptedInitialRefresh.current = true;
          refreshProfile();
          return;
        }

        hasTriggeredRecovery.current = true;
        triggerAuthRecovery({
          instance,
          navigate,
          reason: 'auth_loading_timeout'
        });
      }
    }, 15000);

    return () => clearTimeout(timer);
  }, [userProfile, orgProfile, profileLoading, refreshProfile, instance, navigate]);
  
  useEffect(() => {
    // Wait for profile to finish loading
    if (profileLoading) {
      console.log('[AuthLoading] Waiting for profile to load...');
      return;
    }
    
    // If we arrived from the invitation flow, always force-fetch fresh profile.
    // This ensures we don't accidentally route using a stale cached profile from
    // a prior login (e.g. admin account still in React state from same tab session).
    const forceFetch = location.state?.forceFetch;
    if (forceFetch && !hasTriggeredRefresh.current) {
      hasTriggeredRefresh.current = true;
      console.log('[AuthLoading] forceFetch=true - refreshing profile to get invited user data');
      refreshProfile();
      return;
    }
    
    // Check sessionStorage (profiles stored by invitation/registration flow)
    const cachedOrgProfile = sessionStorage.getItem('orgProfile');
    const cachedUserProfile = sessionStorage.getItem('userProfile');
    
    // If profiles are in sessionStorage but not in UserContext state,
    // UserContext initialized before the profiles were stored - trigger a refresh
    if ((!userProfile || !orgProfile) && (cachedOrgProfile || cachedUserProfile)) {
      if (!hasTriggeredRefresh.current) {
        hasTriggeredRefresh.current = true;
        console.log('[AuthLoading] Profiles in sessionStorage but not in context - triggering refresh');
        refreshProfile();
      }
      return; // Wait for refreshProfile to complete and re-render
    }
    
    // Safety check - if no profile after loading and retries, redirect to login
    if (!userProfile || !orgProfile) {
      if (!hasAttemptedInitialRefresh.current) {
        hasAttemptedInitialRefresh.current = true;
        console.log('[AuthLoading] Missing profile in context - triggering initial refresh');
        refreshProfile();
        return;
      }

      console.warn('[AuthLoading] No profile found after loading - redirecting to login');
      if (!hasTriggeredRecovery.current) {
        hasTriggeredRecovery.current = true;
        triggerAuthRecovery({
          instance,
          navigate,
          reason: 'profile_missing_after_auth'
        });
      }
      return;
    }
    
    console.log('[AuthLoading] Profile loaded:', {
      email: userProfile.email,
      accountType: orgProfile.accountType,
      isTrialExpired: subscriptionState?.isTrialExpired
    });
    
    // Check for trial expiration BEFORE routing
    if (subscriptionState?.isTrialExpired) {
      console.log('[AuthLoading] Trial expired - redirecting to account page');
      navigate('/account', { replace: true });
      return;
    }
    
    // Database is source of truth for account type
    const accountType = normalizeTierName(orgProfile.accountType || '');
    const isConsultant = isConsultantTier(accountType);
    
    // Check for deep link return URL
    const returnUrl = sessionStorage.getItem('returnUrl');
    if (returnUrl) {
      console.log('[AuthLoading] Returning to deep link:', returnUrl);
      sessionStorage.removeItem('returnUrl');
      navigate(returnUrl, { replace: true });
      return;
    }
    
    // Route to correct dashboard based on account type
    if (isConsultant) {
      console.log('[AuthLoading] Routing consultant to dashboard');
      navigate('/consultant-dashboard', { replace: true });
    } else {
      console.log('[AuthLoading] Routing nonprofit to landing');
      navigate('/landing', { replace: true });
    }
  }, [userProfile, orgProfile, subscriptionState, profileLoading, navigate, refreshProfile, location, instance]);
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #173457 0%, #004080 100%)',
        color: '#fff'
      }}
    >
      <CircularProgress 
        size={80} 
        thickness={4}
        sx={{ 
          color: '#fcdf68',
          mb: 4
        }} 
      />
      
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
        Loading Your Workspace
      </Typography>
      
      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
        Setting up your personalized dashboard...
      </Typography>
    </Box>
  );
}
